import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import type { AxiosError } from 'axios';
import { Eye, MicVocal, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
// import {
//   DropdownMenu,
//   DropdownMenuContent,
//   DropdownMenuItem,
//   DropdownMenuLabel,
//   DropdownMenuSeparator,
//   DropdownMenuTrigger,
// } from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { isSubOrSuperAdmin, type UserRole } from '@/lib/constants/roles';
import { cn } from '@/lib/utils/cn';
import {
  useDeletePlan,
  useToggleTtsPlanStatus,
  useTtsPlans,
} from '../api';
import { AddTtsPlanSheet } from '../components/AddTtsPlanSheet';
import { PlanUsersDialog } from '../components/PlanUsersDialog';
import type { TtsPlanRow } from '../types';

export function TtsPlanListPage() {
  const role = useAuthStore((s) => s.role) as UserRole;
  const showOwner = isSubOrSuperAdmin(role);
  const canView = usePermission('TTS Plans.View TTS Plans', BASE_PERMISSION.VIEW);
  const canAdd = usePermission('TTS Plans.Add TTS Plan', BASE_PERMISSION.ADD);

  const list = useTtsPlans();
  const rows = list.data ?? [];

  const toggleStatus = useToggleTtsPlanStatus();
  const del = useDeletePlan();

  const [pendingDelete, setPendingDelete] = useState<TtsPlanRow | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [usersFor, setUsersFor] = useState<TtsPlanRow | null>(null);

  const columns = useMemo<ColumnDef<TtsPlanRow>[]>(
    () => [
      {
        accessorKey: 'planId',
        header: 'ID',
        size: 70,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.planId}
          </span>
        ),
      },
      {
        accessorKey: 'planName',
        header: 'Plan',
        cell: ({ row }) => (
          <span className="flex items-center gap-2 font-medium">
            <MicVocal className="h-3.5 w-3.5 text-muted-foreground" />
            {row.original.planName}
          </span>
        ),
      },
      {
        accessorKey: 'perCharacter',
        header: 'Per char',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {row.original.perCharacter}
          </span>
        ),
      },
      {
        accessorKey: 'perVariable',
        header: 'Per variable',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {row.original.perVariable}
          </span>
        ),
      },
      ...(showOwner
        ? [
            {
              accessorKey: 'username',
              header: 'Created by',
              size: 130,
              cell: ({ row }: { row: { original: TtsPlanRow } }) =>
                row.original.username ?? '—',
            } as ColumnDef<TtsPlanRow>,
            {
              id: 'users',
              header: 'Users',
              size: 90,
              enableSorting: false,
              cell: ({ row }: { row: { original: TtsPlanRow } }) => (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setUsersFor(row.original)}
                >
                  <Eye className="h-4 w-4" />
                  View
                </Button>
              ),
            } as ColumnDef<TtsPlanRow>,
          ]
        : []),
      {
        accessorKey: 'planStatus',
        header: 'Status',
        size: 130,
        enableSorting: false,
        cell: ({ row }) => {
          const enabled = row.original.planStatus === 1;
          return (
            <div className="flex items-center gap-2">
              <Switch
                checked={enabled}
                onCheckedChange={(next) =>
                  toggleStatus.mutate(
                    {
                      planId: row.original.planId,
                      planStatus: next ? 1 : 0,
                    },
                    {
                      onSuccess: (res) =>
                        toast.success(
                          res?.message ??
                            (next ? 'Plan enabled' : 'Plan disabled'),
                        ),
                      onError: (err) =>
                        toast.error(
                          (err as AxiosError<{ message?: string }>)?.response
                            ?.data?.message ?? 'Could not update status.',
                        ),
                    },
                  )
                }
                aria-label={enabled ? 'Disable plan' : 'Enable plan'}
              />
              <span
                className={cn(
                  'text-xs font-medium',
                  enabled ? 'text-emerald-700' : 'text-muted-foreground',
                )}
              >
                {enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          );
        },
      },
      // {
      //   id: 'actions',
      //   header: 'Settings',
      //   size: 80,
      //   enableHiding: false,
      //   enableSorting: false,
      //   cell: ({ row }) => (
      //     <DropdownMenu>
      //       <DropdownMenuTrigger asChild>
      //         <Button variant="ghost" size="icon" className="h-8 w-8">
      //           <Settings2 className="h-4 w-4" />
      //         </Button>
      //       </DropdownMenuTrigger>
      //       <DropdownMenuContent align="end" className="w-44">
      //         <DropdownMenuLabel>{row.original.planName}</DropdownMenuLabel>
      //         <DropdownMenuSeparator />
      //         <DropdownMenuItem
      //           onClick={() => setPendingDelete(row.original)}
      //           className="text-destructive focus:text-destructive"
      //         >
      //           <Trash2 className="h-4 w-4" />
      //           Delete
      //         </DropdownMenuItem>
      //       </DropdownMenuContent>
      //     </DropdownMenu>
      //   ),
      // },
    ],
    [showOwner, toggleStatus],
  );

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="TTS Pricing"
        description="Per-character + per-variable pricing tiers for TTS-enabled users."
        actions={
          canAdd && (
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" />
              New plan
            </Button>
          )
        }
      />

      <DataTable<TtsPlanRow, unknown>
        tableId="plans:tts:list:v1"
        columns={columns}
        data={rows}
        isLoading={list.isLoading}
        searchPlaceholder="Search plans…"
        emptyTitle="No TTS plans"
        emptyDescription='Click "New plan" to define your first TTS pricing tier.'
        getRowId={(row) => String(row.planId)}
        // initialColumnPinning={{ right: ['actions'] }}
        exportFilename="tts-plans.csv"
        pageSize={25}
      />

      <AddTtsPlanSheet open={addOpen} onOpenChange={setAddOpen} />

      <PlanUsersDialog
        open={usersFor !== null}
        onOpenChange={(o) => !o && setUsersFor(null)}
        kind="tts"
        planId={usersFor?.planId ?? null}
        planName={usersFor?.planName}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete TTS plan?"
        description={
          <>
            Plan{' '}
            <span className="font-medium text-foreground">
              {pendingDelete?.planName}
            </span>{' '}
            will be permanently removed. Users currently on this plan keep
            their existing assignment until you reassign them.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          del.mutate(pendingDelete.planId, {
            onSuccess: (res) => {
              toast.success(res?.message ?? `Deleted ${pendingDelete.planName}`);
              setPendingDelete(null);
            },
            onError: () => toast.error('Could not delete the plan.'),
          });
        }}
      />
    </FillPage>
  );
}
